import type { LucideIcon } from "lucide-react";

export interface SegmentedControlOption<T extends string | number> {
  value: T;
  label: string;
  icon?: LucideIcon;
  badge?: string;
  tooltip?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
  ariaLabel?: string;
}

export function SegmentedControl<T extends string | number>({
  value,
  onChange,
  options,
  disabled = false,
  size = "md",
  className = "",
  ariaLabel,
}: SegmentedControlProps<T>) {
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let nextIndex = index;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      nextIndex = (index + 1) % options.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      nextIndex = (index - 1 + options.length) % options.length;
    }

    if (nextIndex !== index && !options[nextIndex].disabled && !disabled) {
      onChange(options[nextIndex].value);
    }
  };

  const isSmall = size === "sm";

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 rounded-xl bg-muted/60 p-1 border border-border/70 select-none ${className}`}
    >
      {options.map((option, index) => {
        const isSelected = value === option.value;
        const Icon = option.icon;
        const isItemDisabled = disabled || option.disabled;

        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={isItemDisabled}
            onClick={() => !isItemDisabled && onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            title={option.tooltip || option.badge || option.label}
            className={`flex items-center justify-center gap-2 rounded-lg font-medium transition-all outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary ${
              isSmall ? "px-2.5 py-1 text-xs" : "px-3.5 py-2 text-sm min-h-[40px]"
            } ${
              isSelected
                ? "bg-card text-foreground shadow-xs font-semibold ring-1 ring-border/50"
                : "text-muted-foreground hover:text-foreground hover:bg-card/40"
            } ${isItemDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            {Icon && (
              <Icon
                className={`${isSmall ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0 ${
                  isSelected ? "text-primary" : ""
                }`}
              />
            )}
            <span className="truncate">{option.label}</span>
            {option.badge && (
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-primary/10 text-primary">
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
